/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'

import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Card from '@mui/material/Card'
import Stack from '@mui/material/Stack'
import Box from '@mui/material/Box'
import Grid from '@mui/material/Unstable_Grid2'
import Paper from '@mui/material/Paper'
import Button from '@mui/material/Button'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import Skeleton from '@mui/material/Skeleton'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Alert from '@mui/material/Alert'
import Divider from '@mui/material/Divider'

import { useBoolean } from 'src/hooks/use-boolean'
import useDebounce from 'src/hooks/use-debounce'
import useEvents from 'src/service/events.service'
import { OptionType } from 'src/components/hook-form/rhf-autocomplete'
import Iconify from 'src/components/iconify'
import DateTimeRangePicker from 'src/components/date-time-range-picker'
import Scrollbar from 'src/components/scrollbar'
import { useSettingsContext } from 'src/components/settings'
import {
  FechamentoData,
  PaymentType,
  PAYMENT_TYPE_LABELS,
  PAYMENT_TYPE_ORDER,
  fetchFechamento,
} from 'src/service/fechamento.service'

import { formatCurrency } from '../orders/format-currency'
import { generateFechamentoPdf } from './generate-fechamento-pdf'

const fmtDateTime = (value?: string | Date | null) => {
  if (!value) return '—'
  const d = new Date(value)
  return new Intl.DateTimeFormat('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(d)
}

export default function FechamentoView() {
  const settings = useSettingsContext()
  const isLoading = useBoolean(false)
  const isGenerating = useBoolean(false)
  const [query, setQuery] = useState('')
  const [eventFilter, setEventFilter] = useState<any>(null)
  const [startDate, setStartDate] = useState<Date | null>(null)
  const [endDate, setEndDate] = useState<Date | null>(null)
  const [data, setData] = useState<FechamentoData | null>(null)
  const searchDebounce = useDebounce(query, 500)

  const { isLoading: loadingEvent, data: dataEvent } = useEvents(
    { page: 1, perPage: 100, sort: undefined },
    { search: searchDebounce },
  )

  const loadData = async (eventId?: string) => {
    const selectedEventId = eventId || eventFilter?.value
    if (!selectedEventId) return

    try {
      isLoading.onTrue()
      const response = await fetchFechamento({
        event: String(selectedEventId),
        startDate,
        endDate,
      })
      setData(response)
    } catch {
      setData(null)
    } finally {
      isLoading.onFalse()
    }
  }

  const handleFilterEvent = (_event: unknown, selectedValue: any) => {
    setEventFilter(selectedValue)
    if (selectedValue?.value) {
      loadData(String(selectedValue.value))
    } else {
      setData(null)
    }
  }

  useEffect(() => {
    if (eventFilter?.value) loadData()
  }, [startDate, endDate])

  const handleGeneratePdf = async () => {
    if (!data || !eventFilter) return
    try {
      isGenerating.onTrue()
      await generateFechamentoPdf({
        eventName: eventFilter.label || data.event?.name || 'Evento',
        data,
        startDate,
        endDate,
      })
    } finally {
      isGenerating.onFalse()
    }
  }

  const hasData = !!data && data.totals.orderCount > 0

  const renderContent = () => {
    if (!eventFilter) return <Placeholder />
    if (isLoading.value) return <LoadingSkeleton />
    if (!hasData || !data) {
      return (
        <Alert severity='info' sx={{ borderRadius: 2 }}>
          Nenhuma venda encontrada para este evento no período selecionado.
        </Alert>
      )
    }

    const { totals } = data

    return (
      <Stack spacing={3}>
        <Grid container spacing={2}>
          <Grid xs={12} sm={6} md={3}>
            <KpiCard
              title='Receita Bruta'
              value={formatCurrency(totals.gross)}
              icon='solar:dollar-minimalistic-bold'
              color='success'
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <KpiCard
              title='Taxas / Deduções'
              value={formatCurrency(totals.tax)}
              icon='solar:calculator-minimalistic-bold'
              color='warning'
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <KpiCard
              title='Receita Líquida'
              value={formatCurrency(totals.net)}
              icon='solar:wallet-money-bold'
              color='primary'
            />
          </Grid>
          <Grid xs={12} sm={6} md={3}>
            <KpiCard
              title='Pedidos / Vendedores'
              value={`${totals.orderCount} / ${totals.sellerCount}`}
              icon='solar:users-group-rounded-bold'
              color='info'
            />
          </Grid>
        </Grid>

        <SectionCard title='Resumo do Resultado (DRE)' icon='solar:document-text-bold'>
          <Stack spacing={1.5}>
            <DreLine label='Receita Bruta (total vendido)' value={formatCurrency(totals.gross)} strong />
            <Divider />
            <Typography variant='caption' color='text.secondary' sx={{ fontWeight: 600 }}>
              (-) Taxas cobradas por forma de pagamento
            </Typography>
            {PAYMENT_TYPE_ORDER.filter((type) => data.paymentTotals[type].tax > 0).map((type) => {
              const slot = data.paymentTotals[type]
              const rate = slot.gross > 0 ? (slot.tax / slot.gross) * 100 : 0
              return (
                <DreLine
                  key={type}
                  label={`Taxa ${PAYMENT_TYPE_LABELS[type]} (média ${rate
                    .toFixed(2)
                    .replace('.', ',')}%)`}
                  value={`- ${formatCurrency(slot.tax)}`}
                  color='error.main'
                />
              )
            })}
            {totals.tax === 0 && (
              <Typography variant='body2' color='text.secondary'>
                Nenhuma taxa cobrada no período.
              </Typography>
            )}
            <DreLine
              label='(=) Total de Taxas'
              value={`- ${formatCurrency(totals.tax)}`}
              color='error.main'
              strong
            />
            <Divider />
            <DreLine
              label='(=) Receita Líquida (após taxas)'
              value={formatCurrency(totals.net)}
              color='success.dark'
              strong
            />
            <Box sx={{ pt: 1 }}>
              <Typography variant='caption' color='text.secondary'>
                Pedidos: {totals.orderCount} · Vendedores: {totals.sellerCount} · Ticket médio:{' '}
                {formatCurrency(totals.averageTicket)}
              </Typography>
            </Box>
          </Stack>
        </SectionCard>

        <SectionCard title='Receita por Forma de Pagamento' icon='solar:card-bold'>
          <Scrollbar>
            <Table size='small' sx={{ minWidth: 640 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Forma de Pagamento</TableCell>
                  <TableCell align='right'>Nº de Vendas</TableCell>
                  <TableCell align='right'>Valor Bruto</TableCell>
                  <TableCell align='right'>Taxa Cobrada</TableCell>
                  <TableCell align='right'>Valor Líquido</TableCell>
                  <TableCell align='right'>% do Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {PAYMENT_TYPE_ORDER.map((type) => {
                  const slot = data.paymentTotals[type]
                  const pct =
                    totals.gross > 0 ? ((slot.gross / totals.gross) * 100).toFixed(1) : '0.0'
                  return (
                    <TableRow key={type}>
                      <TableCell>
                        <Typography variant='body2' fontWeight={600}>
                          {PAYMENT_TYPE_LABELS[type]}
                        </Typography>
                      </TableCell>
                      <TableCell align='right'>{slot.count}</TableCell>
                      <TableCell align='right'>{formatCurrency(slot.gross)}</TableCell>
                      <TableCell align='right' sx={{ color: 'error.main' }}>
                        - {formatCurrency(slot.tax)}
                      </TableCell>
                      <TableCell align='right' sx={{ color: 'success.dark', fontWeight: 600 }}>
                        {formatCurrency(slot.net)}
                      </TableCell>
                      <TableCell align='right' sx={{ color: 'text.secondary' }}>
                        {pct}%
                      </TableCell>
                    </TableRow>
                  )
                })}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell>TOTAL</TableCell>
                  <TableCell align='right'>{totals.orderCount}</TableCell>
                  <TableCell align='right'>{formatCurrency(totals.gross)}</TableCell>
                  <TableCell align='right' sx={{ color: 'error.main' }}>
                    - {formatCurrency(totals.tax)}
                  </TableCell>
                  <TableCell align='right' sx={{ color: 'success.dark' }}>
                    {formatCurrency(totals.net)}
                  </TableCell>
                  <TableCell align='right'>100%</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Scrollbar>
        </SectionCard>

        <SectionCard title='Resumo por Vendedor' icon='solar:users-group-two-rounded-bold'>
          <Scrollbar>
            <Table size='small' sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Vendedor</TableCell>
                  <TableCell align='right'>Pedidos</TableCell>
                  <TableCell align='right'>Bruto</TableCell>
                  <TableCell align='right'>Taxa</TableCell>
                  <TableCell align='right'>Líquido</TableCell>
                  <TableCell align='right'>1ª venda</TableCell>
                  <TableCell align='right'>Última venda</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.sellers.map((seller) => (
                  <TableRow key={seller.sellerId}>
                    <TableCell>
                      <Typography variant='body2' fontWeight={600}>
                        {seller.sellerName}
                      </Typography>
                    </TableCell>
                    <TableCell align='right'>{seller.orderCount}</TableCell>
                    <TableCell align='right'>{formatCurrency(seller.gross)}</TableCell>
                    <TableCell align='right' sx={{ color: 'error.main' }}>
                      - {formatCurrency(seller.tax)}
                    </TableCell>
                    <TableCell align='right' sx={{ color: 'success.dark', fontWeight: 600 }}>
                      {formatCurrency(seller.net)}
                    </TableCell>
                    <TableCell align='right' sx={{ color: 'text.secondary' }}>
                      {fmtDateTime(seller.firstSale)}
                    </TableCell>
                    <TableCell align='right' sx={{ color: 'text.secondary' }}>
                      {fmtDateTime(seller.lastSale)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Scrollbar>
        </SectionCard>

        <SectionCard title='Vendas por Vendedor e Forma de Pagamento' icon='solar:chart-2-bold'>
          <Scrollbar>
            <Table size='small' sx={{ minWidth: 760 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Vendedor</TableCell>
                  {PAYMENT_TYPE_ORDER.map((type) => (
                    <TableCell key={type} align='right'>
                      {PAYMENT_TYPE_LABELS[type]}
                    </TableCell>
                  ))}
                  <TableCell align='right'>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.sellers.map((seller) => (
                  <TableRow key={seller.sellerId}>
                    <TableCell>
                      <Typography variant='body2' fontWeight={600}>
                        {seller.sellerName}
                      </Typography>
                    </TableCell>
                    {PAYMENT_TYPE_ORDER.map((type: PaymentType) => (
                      <TableCell key={type} align='right'>
                        {formatCurrency(seller.payments[type].gross)}
                      </TableCell>
                    ))}
                    <TableCell align='right' sx={{ fontWeight: 600 }}>
                      {formatCurrency(seller.gross)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell>TOTAL</TableCell>
                  {PAYMENT_TYPE_ORDER.map((type: PaymentType) => (
                    <TableCell key={type} align='right'>
                      {formatCurrency(data.paymentTotals[type].gross)}
                    </TableCell>
                  ))}
                  <TableCell align='right'>{formatCurrency(totals.gross)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Scrollbar>
        </SectionCard>

        <SectionCard title='Vendas por Produto e Forma de Pagamento' icon='solar:box-bold'>
          <Scrollbar>
            <Table size='small' sx={{ minWidth: 860 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Produto</TableCell>
                  <TableCell>Categoria</TableCell>
                  <TableCell align='right'>Unid. Vendidas</TableCell>
                  {PAYMENT_TYPE_ORDER.map((type) => (
                    <TableCell key={type} align='right'>
                      {PAYMENT_TYPE_LABELS[type]}
                    </TableCell>
                  ))}
                  <TableCell align='right'>Total</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.products.map((product, index) => (
                  <TableRow key={`${product.productName}-${index}`}>
                    <TableCell>
                      <Typography variant='body2' fontWeight={600}>
                        {product.productName}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ color: 'text.secondary' }}>{product.categoryName}</TableCell>
                    <TableCell align='right'>{product.totalQuantity}</TableCell>
                    {PAYMENT_TYPE_ORDER.map((type: PaymentType) => (
                      <TableCell key={type} align='right'>
                        {formatCurrency(product.payments[type].gross)}
                      </TableCell>
                    ))}
                    <TableCell align='right' sx={{ fontWeight: 600 }}>
                      {formatCurrency(product.totalRevenue)}
                    </TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 700, borderTop: '2px solid', borderColor: 'divider' } }}>
                  <TableCell>TOTAL</TableCell>
                  <TableCell />
                  <TableCell align='right'>
                    {data.products.reduce((acc, p) => acc + p.totalQuantity, 0)}
                  </TableCell>
                  {PAYMENT_TYPE_ORDER.map((type: PaymentType) => (
                    <TableCell key={type} align='right'>
                      {formatCurrency(
                        data.products.reduce((acc, p) => acc + p.payments[type].gross, 0),
                      )}
                    </TableCell>
                  ))}
                  <TableCell align='right'>
                    {formatCurrency(data.products.reduce((acc, p) => acc + p.totalRevenue, 0))}
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </Scrollbar>
        </SectionCard>
      </Stack>
    )
  }

  return (
    <>
      <Helmet>
        <title>Resumão do Evento - Dashboard</title>
      </Helmet>

      <Container maxWidth={settings.themeStretch ? false : 'xl'}>
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: 2,
            px: { xs: 2, sm: 2.5 },
            py: { xs: 1.5, sm: 2 },
            mb: { xs: 2, md: 3 },
            color: 'white',
          }}
        >
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            alignItems={{ xs: 'flex-start', md: 'center' }}
            justifyContent='space-between'
            spacing={2}
          >
            <Stack direction='row' alignItems='center' spacing={1.5}>
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: 1.5,
                  bgcolor: 'rgba(255,255,255,0.2)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Iconify icon='solar:document-text-bold' width={22} />
              </Box>
              <Box>
                <Typography variant='h6' fontWeight={700}>
                  Resumão do Evento
                </Typography>
                <Typography variant='caption' sx={{ opacity: 0.85 }}>
                  Demonstrativo de resultado para encerrar o evento e imprimir
                </Typography>
              </Box>
            </Stack>

            {hasData && (
              <Button
                variant='contained'
                size='small'
                disabled={isGenerating.value}
                onClick={handleGeneratePdf}
                startIcon={<Iconify icon='solar:document-bold' width={18} />}
                sx={{
                  bgcolor: 'rgba(255,255,255,0.95)',
                  color: '#667eea',
                  fontWeight: 700,
                  textTransform: 'none',
                  '&:hover': { bgcolor: 'white' },
                }}
              >
                {isGenerating.value ? 'Gerando PDF...' : 'Gerar Resumão em PDF'}
              </Button>
            )}
          </Stack>
        </Box>

        <Card sx={{ p: 2.5, mb: 3, borderRadius: 2 }}>
          <Stack spacing={2}>
            <Stack direction='row' alignItems='center' spacing={1}>
              <Iconify icon='solar:filter-bold' width={18} sx={{ color: 'text.secondary' }} />
              <Typography variant='subtitle2' fontWeight={700}>
                Filtros
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid xs={12} md={6}>
                <Autocomplete
                  size='small'
                  options={
                    dataEvent?.data?.map((option) => ({
                      label: option?.name,
                      value: option?._id,
                    })) || []
                  }
                  isOptionEqualToValue={(option: any, value: any) => option?.value === value?.value}
                  getOptionLabel={(option: OptionType | string) => (option as OptionType)?.label}
                  loading={loadingEvent}
                  value={eventFilter}
                  onChange={handleFilterEvent}
                  onInputChange={(_e, value, reason) => {
                    if (reason === 'input') setQuery(value)
                    else if (reason === 'clear' || reason === 'reset') setQuery('')
                  }}
                  // Busca é server-side: não deixa o MUI re-filtrar a lista pelo texto do input.
                  filterOptions={(x) => x}
                  renderInput={(params) => (
                    <TextField {...params} label='Evento *' placeholder='Selecione o evento' />
                  )}
                />
              </Grid>
              <Grid xs={12} md={6}>
                <DateTimeRangePicker
                  label='Período'
                  size='small'
                  startDate={startDate}
                  endDate={endDate}
                  onChange={(start, end) => {
                    setStartDate(start)
                    setEndDate(end)
                  }}
                />
              </Grid>
            </Grid>
          </Stack>
        </Card>

        {renderContent()}
      </Container>
    </>
  )
}

function DreLine({
  label,
  value,
  color,
  strong,
}: {
  label: string
  value: string
  color?: string
  strong?: boolean
}) {
  return (
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Typography variant={strong ? 'subtitle2' : 'body2'} fontWeight={strong ? 700 : 400}>
        {label}
      </Typography>
      <Typography
        variant={strong ? 'subtitle1' : 'body2'}
        fontWeight={strong ? 700 : 500}
        sx={{ color: color || 'text.primary' }}
      >
        {value}
      </Typography>
    </Stack>
  )
}

function KpiCard({
  title,
  value,
  icon,
  color,
}: {
  title: string
  value: string
  icon: string
  color: 'success' | 'warning' | 'primary' | 'info'
}) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: '1px solid', borderColor: `${color}.lighter` }}>
      <Stack direction='row' alignItems='center' spacing={1.5}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 1.5,
            bgcolor: `${color}.lighter`,
            color: `${color}.main`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Iconify icon={icon} width={20} />
        </Box>
        <Box>
          <Typography variant='caption' color='text.secondary'>
            {title}
          </Typography>
          <Typography variant='h6' fontWeight={700} sx={{ lineHeight: 1.2 }}>
            {value}
          </Typography>
        </Box>
      </Stack>
    </Paper>
  )
}

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string
  icon: string
  children: React.ReactNode
}) {
  return (
    <Card sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack
        direction='row'
        alignItems='center'
        spacing={1.25}
        sx={{
          px: 2.5,
          py: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          bgcolor: 'grey.50',
        }}
      >
        <Iconify icon={icon} width={18} sx={{ color: 'primary.main' }} />
        <Typography variant='subtitle2' fontWeight={700}>
          {title}
        </Typography>
      </Stack>
      <Box sx={{ p: 2.5 }}>{children}</Box>
    </Card>
  )
}

function Placeholder() {
  return (
    <Card
      sx={{
        p: { xs: 4, md: 8 },
        borderRadius: 3,
        textAlign: 'center',
        border: '2px dashed',
        borderColor: 'primary.light',
        background:
          'linear-gradient(135deg, rgba(99, 102, 241, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
      }}
    >
      <Iconify icon='solar:document-text-bold' width={48} sx={{ color: 'primary.main', mb: 2 }} />
      <Typography variant='h6' fontWeight={700} gutterBottom>
        Selecione um evento
      </Typography>
      <Typography variant='body2' color='text.secondary' sx={{ maxWidth: 480, mx: 'auto' }}>
        Escolha o evento acima para visualizar o DRE e gerar o demonstrativo de resultado em PDF,
        com receita por forma de pagamento, taxas e o detalhamento de cada vendedor.
      </Typography>
    </Card>
  )
}

function LoadingSkeleton() {
  return (
    <Stack spacing={2}>
      <Grid container spacing={2}>
        {[1, 2, 3, 4].map((item) => (
          <Grid xs={12} sm={6} md={3} key={item}>
            <Skeleton variant='rounded' height={88} />
          </Grid>
        ))}
      </Grid>
      <Skeleton variant='rounded' height={220} />
      <Skeleton variant='rounded' height={280} />
    </Stack>
  )
}
